import { ConfirmModal, Toasts, useModalContext } from '@fourthwall/components'

import logoSrc from '../../../assets/signets/streamlabs.svg'
import { useAppConnectEvent } from '../../../hooks/useAppConnectEvent'
import { useAppConnectedEvent } from '../../../hooks/useAppConnectedEvent'
import type { AppsStreamlabsViewProps } from '../AppsStreamlabsView'
import { useDisconnectStreamlabsMutation } from '../queries/useDisconnectStreamlabsMutation'
import { useStreamlabsAuthUrlQuery } from '../queries/useStreamlabsAuthUrlQuery'

export const useStreamlabs = () => {
  const { streamlabsAuthUrlQuery } = useStreamlabsAuthUrlQuery()
  const { disconnectStreamlabsMutation } = useDisconnectStreamlabsMutation()
  const { open } = useModalContext()
  const appConnectEvent = useAppConnectEvent('stream_labs_alerts')

  useAppConnectedEvent('stream_labs_alerts')

  const getStatus = (): 'CONNECTED' | 'NOT_CONNECTED' | undefined => {
    if (!streamlabsAuthUrlQuery.data) return undefined

    if (streamlabsAuthUrlQuery.data.streamLabsAuthenticationUrl) return 'NOT_CONNECTED'

    if (!streamlabsAuthUrlQuery.data.streamLabsAuthenticationUrl) return 'CONNECTED'
  }

  const status = getStatus()

  const getConnectBar = (): AppsStreamlabsViewProps['connectBar'] => {
    const connectBarBase = {
      logoSrc,
      description: 'Connect to your Streamlabs account',
      disabled: false,
    }

    if (!status) return undefined

    if (status === 'NOT_CONNECTED') {
      if (!streamlabsAuthUrlQuery.data) return

      const onConnect = () => {
        appConnectEvent.trigger()

        if (streamlabsAuthUrlQuery.data.streamLabsAuthenticationUrl) {
          const url = new URL(streamlabsAuthUrlQuery.data.streamLabsAuthenticationUrl)
          url.searchParams.append('connected', '')

          window.location.assign(url.toString())
        }
      }

      return {
        ...connectBarBase,
        onConnect,
      }
    }

    if (status === 'CONNECTED') {
      const onDisconnect = () => {
        open(ConfirmModal, {
          title: 'Are you sure you want to disconnect from Streamlabs?',
          confirmLabel: 'Yes, disconnect',
          confirmAppearance: 'destructive',
          onConfirm: () => {
            const toastId = 'disconnectStreamlabsMutation'

            Toasts.notify('Disconnecting from Streamlabs...', {
              toastId,
              type: 'info',
              autoClose: false,
            })

            disconnectStreamlabsMutation.mutate([], {
              onSuccess: () => {
                streamlabsAuthUrlQuery.refetch()

                Toasts.notify('Disconnected from Streamlabs successfully.', { type: 'success' })
                Toasts.dismiss(toastId)
              },
            })
          },
        })
      }

      return {
        ...connectBarBase,
        disabled: disconnectStreamlabsMutation.isLoading,
        onDisconnect,
      }
    }
  }

  return {
    streamlabs: {
      status,
      connectBar: getConnectBar(),
    },
  }
}
